import React, {useCallback, useState} from 'react';
import Carousel from 'nuka-carousel';
import {VerticalSpacer, VideoLightbox} from '../../elements';
import styled from 'styled-components';
import {AccentColorOptions} from 'epic-ue-theme';
import {ArrowControls} from './FeaturesCarousel.controls';
import {FeaturesCarouselSlide, FeaturesCarouselSlideProps} from './FeaturesCarousel.slide';
import {FeaturesCarouselTrack} from './FeaturesCarousel.track';
import {AnimateOnViewWrap, Spotlight} from '../../wraps';

const Styled = styled.div<{hasCaption: boolean}>`
    .slider {
        &:focus {
            outline: none;
        }
    }
    .slider-control-centerleft,
    .slider-control-centerright {
        height: 100%;
    }

    ${(p) => p.theme.breakpoints.up('md')} {
        .slider-list {
            // important used here to override inline styles being set by nuka
            // add height of image credits div
            margin-bottom: ${(p) => (p.hasCaption ? '45px !important' : 'initial')};
        }
    }
`;

export interface FeaturesCarouselProps {
    slides: FeaturesCarouselSlideProps[];
    accentColor?: AccentColorOptions;
    isLarge?: boolean;
    noSlideTopSpacing?: boolean;
}

export const FeaturesCarousel = ({
    slides = [],
    accentColor,
    isLarge,
    noSlideTopSpacing
}: FeaturesCarouselProps): JSX.Element => {
    const [isOpen, setIsOpen] = useState(false);
    const [lightboxMediaOptions, setLightboxMediaOptions] = useState([]);
    const toggleLightbox = useCallback(
        (lightboxOptions) => {
            setIsOpen(!isOpen);
            setLightboxMediaOptions(lightboxOptions);
        },
        [setIsOpen, isOpen]
    );

    const [slideIndex, setSlideIndex] = useState(0);
    const beforeSlideCallback = useCallback(
        (_, nextIndex) => {
            setSlideIndex(nextIndex);
        },
        [setSlideIndex]
    );
    const tabMenuItemClick = useCallback(
        (newIndex) => {
            setSlideIndex(newIndex);
        },
        [setSlideIndex]
    );

    const leftArrowDisable = slideIndex === 0;
    const rightArrowDisable = slideIndex === slides.length - 1;

    const sidePadding = isLarge ? 100 : 60;

    const currentSlide = slides[slideIndex];
    let sourceImage;
    if (currentSlide && currentSlide.image) {
        const currentImg = currentSlide.image || {};
        if (currentImg.src) {
            sourceImage = currentImg.src;
        }
    }

    return (
        <Styled
            hasCaption={
                typeof currentSlide?.caption?.text !== 'undefined' &&
                currentSlide?.caption?.text?.length > 0
            }>
            <AnimateOnViewWrap translateY={30}>
                <FeaturesCarouselTrack
                    tabMenuItemClick={tabMenuItemClick}
                    slides={slides}
                    slideIndex={slideIndex}
                    accentColor={accentColor}
                />
            </AnimateOnViewWrap>

            <VerticalSpacer variant="custom" spacing={9} />

            <AnimateOnViewWrap translateY={30}>
                <Spotlight
                    image={sourceImage}
                    accentColor={accentColor}
                    variant="medium"
                    shape="circular"
                    offsetRatio={[0.15, -0.1]}
                    optMaxWidth={900}>
                    <Carousel
                        heightMode="max"
                        easing="easeExpOut"
                        dragging={false}
                        enableKeyboardControls
                        cellSpacing={5}
                        framePadding={`0 ${sidePadding}px`}
                        slideIndex={slideIndex}
                        beforeSlide={beforeSlideCallback}
                        renderBottomCenterControls={null}
                        renderCenterLeftControls={({previousSlide}) => (
                            <ArrowControls
                                small={!isLarge}
                                onClick={previousSlide}
                                disabled={leftArrowDisable}
                                accentColor={accentColor}
                                type="left"
                            />
                        )}
                        renderCenterRightControls={({nextSlide}) => (
                            <ArrowControls
                                small={!isLarge}
                                onClick={nextSlide}
                                disabled={rightArrowDisable}
                                accentColor={accentColor}
                                type="right"
                            />
                        )}>
                        {slides.map((slide = {}, i) => {
                            const activeSlide = i === slideIndex;
                            return (
                                <FeaturesCarouselSlide
                                    key={`f-car-slide-${slide.slideName || slide.title || i}`}
                                    activeSlide={activeSlide}
                                    {...slide}
                                    noTopSpacing={slide.noTopSpacing || noSlideTopSpacing}
                                    accentColor={accentColor}
                                    toggleLightbox={toggleLightbox}
                                />
                            );
                        })}
                    </Carousel>
                </Spotlight>
                {lightboxMediaOptions && (
                    <VideoLightbox
                        isOpen={isOpen}
                        toggleLightbox={toggleLightbox}
                        mediaOptions={lightboxMediaOptions}
                    />
                )}
            </AnimateOnViewWrap>
        </Styled>
    );
};
